import { api, type DeckRecordWire } from '../api-client.js';
import type { DeckRecord } from './types.js';

/**
 * API-backed deck storage. Talks to /api/decks for the persisted
 * collection. The legacy localStorage shape is still readable via
 * `readLegacyLocalDecks` so the deck-list page can offer a one-time
 * migration to the user.
 *
 * `DeckRecord` (the in-app type) is identical to `DeckRecordWire`
 * with one extra field — we keep the shape stable and the project
 * function strips/normalises any drift.
 */

const LEGACY_STORAGE_KEY = 'ecg.decks.v1';

function project(wire: DeckRecordWire): DeckRecord {
  return {
    id: wire.id,
    gameKey: wire.gameKey,
    formatKey: wire.formatKey,
    name: wire.name,
    entries: wire.entries.map((e) => ({ cardId: e.cardId, qty: e.qty })),
    createdAt: wire.createdAt,
    updatedAt: wire.updatedAt,
  };
}

export async function listDecks(gameKey: string): Promise<DeckRecord[]> {
  const wire = await api.listDecks(gameKey);
  return wire.map(project);
}

export async function loadDeck(_gameKey: string, deckId: string): Promise<DeckRecord | null> {
  try {
    const wire = await api.getDeck(deckId);
    return project(wire);
  } catch {
    return null;
  }
}

/**
 * Creates a brand-new deck on the server. Used when the deck builder
 * leaves the `/new` URL after the first mutation. Returns the deck
 * the server minted (with its real id).
 */
export async function createDeck(input: {
  gameKey: string;
  formatKey: string;
  name: string;
  entries: DeckRecord['entries'];
}): Promise<DeckRecord> {
  return project(await api.createDeck(input));
}

/**
 * Updates a deck. The server validates legality on every write.
 * Throws on validation failure — the caller should surface the error.
 */
export async function updateDeck(
  deckId: string,
  patch: Partial<{ name: string; formatKey: string; entries: DeckRecord['entries'] }>,
): Promise<DeckRecord> {
  return project(await api.updateDeck(deckId, patch));
}

export async function deleteDeck(_gameKey: string, deckId: string): Promise<void> {
  await api.deleteDeck(deckId);
}

/**
 * Browser-side uuid for placeholder deck ids while a 'new' deck has
 * not yet been persisted. Once the user adds their first card, we
 * call `createDeck` and replace the local id with the server's id.
 */
export function newDeckId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `local_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`
  );
}

// ---------------------------------------------------------------------------
// Legacy-localStorage migration helpers — read-only views of the old
// shape so the deck-list page can offer to upload pre-persistence
// decks. Once the user accepts the migration, `clearLegacyLocalDecks`
// nukes the local copy.
// ---------------------------------------------------------------------------

type LegacyShape = {
  [gameKey: string]: { [deckId: string]: DeckRecord };
};

function readLegacy(): LegacyShape {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object') {
      return parsed as LegacyShape;
    }
  } catch {
    // ignore parse errors — treat as no legacy data
  }
  return {};
}

export function readLegacyLocalDecks(gameKey: string): DeckRecord[] {
  const legacy = readLegacy();
  const decks = legacy[gameKey] ?? {};
  return Object.values(decks);
}

export function clearLegacyLocalDecks(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(LEGACY_STORAGE_KEY);
}

export function hasLegacyLocalDecks(): boolean {
  const legacy = readLegacy();
  return Object.values(legacy).some(
    (perGame) => typeof perGame === 'object' && perGame !== null && Object.keys(perGame).length > 0,
  );
}
